import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ConfigProvider, message, Typography, Space } from 'antd';
import enUS from 'antd/lib/locale/en_US';
import AntdProForm, { ModalForm, ProFormSelect } from '@ant-design/pro-form';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import {
  ASSIGN_PROJECT_APPLICATION_ENDORSER,
  GET_PROJECT_APPLICATION,
} from 'gql/projectApplications';
import { GET_USERS_FOR_SELECT_USER } from 'gql/users';
import { displayFacDept } from 'utils';

const { Text } = Typography;

const AssignEndoserModal = ({
  endorsementSequence,
  stepSequence,
  buttonText,
  initialValues,
}) => {
  const { id } = useParams();
  const [form] = AntdProForm.useForm();
  const [assign] = useMutation(ASSIGN_PROJECT_APPLICATION_ENDORSER, {
    refetchQueries: [
      {
        query: GET_PROJECT_APPLICATION,
        variables: { id },
      },
    ],
    onCompleted: () => message.success('Andorser assigned successfully'),
  });
  const { loading, refetch } = useQuery(GET_USERS_FOR_SELECT_USER, {
    variables: {
      state: 'ACTIVE',
      ids: initialValues ? [initialValues] : [],
      first: 1,
    },
  });
  const init = useRef(true);

  return (
    <ConfigProvider locale={enUS}>
      <ModalForm
        form={form}
        initialValues={{ user: initialValues }}
        title="Assign Endorser"
        trigger={
          <Button type="primary" style={{ marginLeft: '16px' }}>
            {buttonText}
          </Button>
        }
        modalProps={{
          onCancel: () => {
            form.resetFields();
            init.current = true;
          },
        }}
        onFinish={async ({ user }) => {
          await assign({
            variables: {
              input: {
                id,
                stepSequence,
                endorsementSequence,
                user,
              },
            },
          });
          form.resetFields();
          init.current = true;
          return true;
        }}
      >
        <ProFormSelect
          showSearch
          width="xl"
          label="User"
          loading={true}
          placeholder="type to search user"
          debounceTime={process.env.REACT_APP_DEBOUNCE_TIME}
          request={async ({ keyWords }) => {
            if (init.current) {
              const refetchData = await refetch({
                state: 'ACTIVE',
                keyword: keyWords,
                ids: initialValues ? [initialValues] : [],
                first: 1,
              });
              const results = refetchData?.data?.users.map((user) => {
                const { id, name, netId } = user;
                return {
                  value: id,
                  label: `${name} (${netId})`,
                  user,
                };
              });
              init.current = false;
              return results;
            }
            const refetchData = await refetch({
              state: 'ACTIVE',
              keyword: keyWords,
              ids: [],
              first: 10,
            });
            const results = refetchData?.data?.users.map((user) => {
              const { id, name, netId } = user;
              return {
                value: id,
                label: `${name} (${netId})`,
                user,
              };
            });
            if (!loading && keyWords === '') return results;

            return results;
          }}
          rules={[
            {
              required: true,
              message: 'Please select user!',
            },
          ]}
          fieldProps={{
            filterOption: false,
            optionItemRender: ({ user: { department, faculty }, label }) => {
              return (
                <Space direction="vertical">
                  {label}
                  <Text type="secondary">
                    {displayFacDept({
                      department: department,
                      faculty: faculty,
                      prefix: false,
                    })}
                  </Text>
                </Space>
              );
            },
          }}
          name="user"
        />
      </ModalForm>
    </ConfigProvider>
  );
};

AssignEndoserModal.defaultProps = {
  buttonText: 'Assign endorser',
};

AssignEndoserModal.propTypes = {
  stepSequence: PropTypes.number.isRequired,
  endorsementSequence: PropTypes.number.isRequired,
  buttonText: PropTypes.node.isRequired,
  initialValues: PropTypes.string,
};

export default AssignEndoserModal;
